import { ApiClientMiddleware, RetryFlaggedInit } from './api-client-middleware';

export interface RefreshMiddlewareOptions {
  /**
   * Performs a coordinated token refresh. Must be deduplicated across concurrent callers
   * (multiple in-flight API calls hitting 401 at the same time should share one refresh).
   * Resolve `true` on success (a fresh token is now available); resolve `false` (or throw)
   * on failure (the caller will be logged out via {@link onRefreshFailure}).
   */
  coordinateRefresh(): Promise<boolean>;

  /**
   * Invoked when the refresh resolves falsy (or throws), and when a request that was already
   * retried after a refresh comes back with another 401. Apps typically call `logout()` or
   * redirect to the login flow here.
   */
  onRefreshFailure?(): void | Promise<void>;

  /**
   * Substrings: when the request URL contains any of these, the 401 is returned to the
   * caller without attempting a refresh. Apps include `/auth` so refresh-token / login
   * endpoints never recurse through this middleware.
   */
  skipForUrlsContaining?: readonly string[];
}

/**
 * Post-middleware that handles 401 responses by coordinating a single token refresh, then
 * retrying the original request once. A second 401 (or refresh failure) hands control to
 * the `onRefreshFailure` callback.
 *
 * Uses a `_refreshed` marker on `RequestInit` to detect the retry, so two parallel callers
 * that already retried once won't loop. The marker is invisible to the generated client.
 */
export function createRefreshMiddleware(options: RefreshMiddlewareOptions): ApiClientMiddleware {
  const skipUrls = options.skipForUrlsContaining ?? [];

  return {
    post: async (context) => {
      if (context.response.status !== 401) {
        return;
      }

      if (skipUrls.some((segment) => context.url.includes(segment))) {
        return;
      }

      if ((context.init as RetryFlaggedInit)._refreshed) {
        await options.onRefreshFailure?.();
        return context.response;
      }

      let success = false;
      try {
        success = await options.coordinateRefresh();
      } catch (error) {
        console.error('[api-client] refresh error', error);
      }

      if (!success) {
        await options.onRefreshFailure?.();
        return context.response;
      }

      // Re-enter the fetch through the client's own fetch wrapper so pre-middleware runs
      // again and picks up the freshly stored token. Marked as `_refreshed` so a second 401
      // on the retry falls through to `onRefreshFailure` instead of looping.
      const retryInit: RetryFlaggedInit = { ...context.init, _refreshed: true };
      return context.fetch(context.url, retryInit);
    },
  };
}
